export type UserLevel = 'FREE' | 'STANDARD' | 'PROFESSIONAL'

export interface User {
  userId: string
  name: string
  email: string
  userLevel: UserLevel
  // 백엔드 Jackson이 boolean isAdmin 필드를 "is" 접두사를 제거한 "admin" 키로 직렬화함
  // (MeshData.isSample → sample과 동일한 기존 관례를 따름)
  admin: boolean
}

export interface AuthState {
  token: string | null
  user: User | null
  expiresAt: number | null
}

export type ConversionStatus = 'PENDING' | 'CONVERTING' | 'SUCCESS' | 'FAILED'

export interface MeshData {
  id: number
  name: string
  description: string | null
  originalFileName: string
  status: ConversionStatus
  tileUrl: string | null
  sample: boolean
  originalFileSize: number | null
  hasOriginalFile: boolean
  createdAt: string
  convertedAt: string | null
}

export interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export interface UserProfile {
  userId: string
  name: string
  email: string
  organization: string | null
  loginType: 'LOCAL' | 'NAVER'
  userLevel: UserLevel
  createdAt: string
  admin: boolean
}

export interface PeriodStat {
  year: number
  period: number
  count: number
}

export interface DataItemStat {
  id: number
  tileSize: number
}

export interface UserStats {
  totalCount: number
  successCount: number
  failedCount: number
  convertingCount: number
  pendingCount: number
  totalOriginalSize: number
  storageSize: number
  tileNetworkUsage: number
  dailyStats: PeriodStat[]
  monthlyStats: PeriodStat[]
  weeklyStats: PeriodStat[]
  dataSizes: DataItemStat[]
}

export interface DailyStatPoint {
  date: string
  bytes: number
}

export interface StreamingStats {
  totalBytes: number
  dailyStats: DailyStatPoint[]
}

export interface QuotaItem {
  resourceType: string
  usedBytes: number
  limitBytes: number
  usedGib: number
  limitGib: number
  percentage: number
  exceeded: boolean
}

export interface UsageQuota {
  billingCycle: string
  conversion: QuotaItem
  storage: QuotaItem
  streaming: QuotaItem
  downloadPolicy: string
}

export interface BillingSummary {
  userLevel: UserLevel
  levelLabel: string
  monthlyFee: number
  billingCycle: string
  nextBillingDate: string
  hasPaymentMethod: boolean
  scheduledLevel: UserLevel | null
  scheduledLevelLabel: string | null
}

export interface Invoice {
  billingCycle: string
  userLevel: UserLevel
  levelLabel: string
  basicFee: number
  status: 'FREE' | 'PENDING' | 'PAID'
}

export type InquiryCategory = 'TECH_SUPPORT' | 'BILLING' | 'PARTNERSHIP' | 'OTHER'

export const INQUIRY_CATEGORY_OPTIONS = [
  { value: 'TECH_SUPPORT', labelKey: 'contact.category.TECH_SUPPORT' },
  { value: 'BILLING', labelKey: 'contact.category.BILLING' },
  { value: 'PARTNERSHIP', labelKey: 'contact.category.PARTNERSHIP' },
  { value: 'OTHER', labelKey: 'contact.category.OTHER' },
] as const satisfies readonly { value: InquiryCategory; labelKey: string }[]

export interface ContactMessage {
  id: number
  title: string
  content?: string
  category: InquiryCategory
  categoryLabel: string
  contactEmail?: string
  authorName?: string
  authorEmail?: string
  hasAttachment: boolean
  attachmentFileName?: string
  createdAt: string
}

export type QnaCategory = 'SERVICE' | 'BILLING' | 'TECH_SUPPORT' | 'ACCOUNT'

export const QNA_CATEGORY_OPTIONS = [
  { value: 'SERVICE', labelKey: 'qna.category.SERVICE' },
  { value: 'BILLING', labelKey: 'qna.category.BILLING' },
  { value: 'TECH_SUPPORT', labelKey: 'qna.category.TECH_SUPPORT' },
  { value: 'ACCOUNT', labelKey: 'qna.category.ACCOUNT' },
] as const satisfies readonly { value: QnaCategory; labelKey: string }[]

export interface QnaEntry {
  id: number
  question: string
  answer: string
  /** 영어 번역(없으면 null → 한국어 본문 표시) */
  questionEn: string | null
  answerEn: string | null
  category: QnaCategory
  categoryLabel: string
  createdAt: string
  updatedAt: string | null
}

/** 메쉬 데이터에 딸려 서버에 저장된 유량(침수) 시뮬레이션 결과(.mago) */
export interface SimulationResult {
  id: number
  meshDataId: number
  name: string
  fileName: string | null
  sizeBytes: number
  createdAt: string
}
